// The funnel's Impressions / Listing views steps are only meaningful when the
// RealAdvisor portal was actually tracking while the listing was online.
//
// GA4 has emitted listing impressions since 2025-03-14, but the serving table
// the CRM reads (ClickHouse `listing_impressions`, fed from BigQuery) was seeded
// with a 365-day backfill and holds data from this day onwards. Anything
// published and taken down before it reports 0 for lack of data, not for lack
// of interest.
export const IMPRESSIONS_TRACKED_SINCE = new Date('2025-06-19T00:00:00Z');

export type RealAdvisorPortalListing = {
  live: boolean;
  // Accumulated while offline: summed each time the listing is taken down.
  milliseconds_online: number;
  // Last modification (a schema-wide trigger bumps it on any UPDATE). For a
  // listing that is not live this is usually the moment it went offline; a
  // later edit of the row only errs towards showing the steps.
  updated_at: string;
};

type Options = {
  portalListings: RealAdvisorPortalListing[];
  // Start of the window the impressions query covers.
  windowStart: Date;
  impressionsLoading: boolean;
  totalImpressions: number;
  totalViews: number;
};

export const wasPublishedOnRealAdvisor = (
  portalListings: RealAdvisorPortalListing[],
): boolean =>
  portalListings.some(
    listing => listing.live || listing.milliseconds_online > 0,
  );

// Whether the portal steps should be drawn. Any real count wins, whatever the
// portal row says. Otherwise hidden when the listing was never on RealAdvisor,
// or when every publication ended before the impressions data starts (so the
// zeros say nothing). While the counts are still loading a published listing
// keeps its steps so they don't pop in.
export const shouldShowPortalSteps = ({
  portalListings,
  windowStart,
  impressionsLoading,
  totalImpressions,
  totalViews,
}: Options): boolean => {
  if (totalImpressions + totalViews > 0) {
    return true;
  }
  if (!wasPublishedOnRealAdvisor(portalListings)) {
    return false;
  }
  if (impressionsLoading) {
    return true;
  }
  const dataStart = new Date(
    Math.max(IMPRESSIONS_TRACKED_SINCE.getTime(), windowStart.getTime()),
  );
  return portalListings.some(
    listing =>
      listing.live ||
      new Date(listing.updated_at).getTime() >= dataStart.getTime(),
  );
};
